import dayjs from "dayjs";

/** Default matches previous hard-coded 90-day (3 month) near-expiry window. */
export const DEFAULT_EXPIRY_THRESHOLD_MONTHS = 3;

export const EXPIRY_THRESHOLD_PRESET_MONTHS = [1, 2, 3];

const DAYS_PER_MONTH = 30;

export const monthsToThresholdDays = (months) => {
  const value = Number(months);
  if (!Number.isFinite(value) || value <= 0) {
    return DEFAULT_EXPIRY_THRESHOLD_MONTHS * DAYS_PER_MONTH;
  }
  return Math.round(value * DAYS_PER_MONTH);
};

export const parseExpiryThresholdMonths = (raw, fallback = DEFAULT_EXPIRY_THRESHOLD_MONTHS) => {
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0 || value > 60) {
    return fallback;
  }
  return value;
};

export const getExpiryThresholdStorageKey = (organizationId) =>
  organizationId
    ? `hms_pharmacy_expiry_threshold_months_${organizationId}`
    : "hms_pharmacy_expiry_threshold_months_global";

export const readExpiryThresholdMonthsFromStorage = (organizationId) => {
  try {
    const stored = localStorage.getItem(getExpiryThresholdStorageKey(organizationId));
    if (stored == null || stored === "") return null;
    return parseExpiryThresholdMonths(stored, null);
  } catch {
    return null;
  }
};

export const writeExpiryThresholdMonthsToStorage = (organizationId, months) => {
  const safe = parseExpiryThresholdMonths(months);
  localStorage.setItem(getExpiryThresholdStorageKey(organizationId), String(safe));
  return safe;
};

export const isExpiredExpiryDate = (expiryDate, today = dayjs()) => {
  if (!expiryDate) return false;
  return dayjs(expiryDate).isBefore(today, "day");
};

/** True when expiry is today or in the future but within the configured month window. */
export const isNearExpiryDate = (expiryDate, thresholdMonths = DEFAULT_EXPIRY_THRESHOLD_MONTHS) => {
  if (!expiryDate) return false;
  const today = dayjs().startOf("day");
  const expiry = dayjs(expiryDate).startOf("day");
  const daysUntilExpiry = expiry.diff(today, "day");
  const thresholdDays = monthsToThresholdDays(thresholdMonths);
  return daysUntilExpiry >= 0 && daysUntilExpiry <= thresholdDays;
};

/**
 * @returns {'expired'|'nearing'|'good'|'unknown'}
 */
export const getMedicineExpiryStatus = (
  expiryDate,
  thresholdMonths = DEFAULT_EXPIRY_THRESHOLD_MONTHS
) => {
  if (!expiryDate) return "unknown";
  if (isExpiredExpiryDate(expiryDate)) return "expired";
  if (isNearExpiryDate(expiryDate, thresholdMonths)) return "nearing";
  return "good";
};

export const formatExpiryThresholdLabel = (months) => {
  const value = parseExpiryThresholdMonths(months);
  return value === 1 ? "1 month" : `${value} months`;
};

/** Fired when pharmacy admin saves near-expiry threshold (syncs all hook instances). */
export const PHARMACY_EXPIRY_THRESHOLD_CHANGED = "hms-pharmacy-expiry-threshold-changed";

export const notifyPharmacyExpiryThresholdChanged = (months) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(PHARMACY_EXPIRY_THRESHOLD_CHANGED, {
      detail: { months: parseExpiryThresholdMonths(months) },
    })
  );
};

/**
 * Recompute group-level expiry flags using the current threshold (no API refetch).
 */
export const applyExpiryFlagsToGroupedProducts = (
  groups,
  thresholdMonths = DEFAULT_EXPIRY_THRESHOLD_MONTHS
) => {
  if (!Array.isArray(groups)) return [];

  return groups.map((group) => {
    const records = group.records || [];
    let hasExpiredStock = false;
    let hasNearingExpiry = false;
    let hasGoodStock = false;
    let worstExpiryStatus = "good";

    records.forEach((record) => {
      const expiryDateValue =
        record.expirationDate || record.expiryDate || record.expiry;
      const expired = isExpiredExpiryDate(expiryDateValue);
      const expiryStatus = getMedicineExpiryStatus(expiryDateValue, thresholdMonths);

      if (expired) hasExpiredStock = true;
      if (expiryStatus === "nearing") hasNearingExpiry = true;
      if (expiryStatus === "good") hasGoodStock = true;

      if (expiryStatus === "expired") {
        worstExpiryStatus = "expired";
      } else if (
        expiryStatus === "nearing" &&
        worstExpiryStatus !== "expired"
      ) {
        worstExpiryStatus = "nearing";
      } else if (
        expiryStatus === "good" &&
        worstExpiryStatus !== "expired" &&
        worstExpiryStatus !== "nearing"
      ) {
        worstExpiryStatus = "good";
      }
    });

    const totalQuantity = records.reduce((sum, record) => {
      const expiryDateValue =
        record.expirationDate || record.expiryDate || record.expiry;
      const expired = isExpiredExpiryDate(expiryDateValue);
      return sum + (expired ? 0 : parseInt(record.quantity, 10) || 0);
    }, 0);

    return {
      ...group,
      totalQuantity,
      hasExpiredStock,
      hasNearingExpiry,
      hasGoodStock,
      worstExpiryStatus,
    };
  });
};
